/**
 * @file ReportConfig.jsx
 * @description Report configuration page — lists scheduled report deliveries
 *              and hosts the add/edit dialog. Run times are interpreted in the
 *              client time zone set in Configuration → Time Settings.
 * @component ReportConfig
 * @exports ReportConfig
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Alert, Box, CircularProgress, IconButton, MenuItem, Select, Snackbar,
    Switch, Tooltip, Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";

import {
    GRANULARITY_LABEL, TIME_RANGE_LABEL, NEW_SCHEDULE, RECURRENCE,
    REPORT_STRINGS, ROWS_PER_PAGE, DEFAULT_TIME_ZONE_KEY,
    describeRecurrence, resolveTimeZone, clockInZone, formatClock,
} from "../../common/constants/reportModule/reportConstants.js";
import {
    fetchReportCatalog, fetchReportSchedules, fetchClientTimeZone,
    createReportSchedule, updateReportSchedule, toggleReportSchedule,
    deleteReportSchedule, runReportNow,
} from "../../services/reportModule/reportServices.js";
import ReportScheduleDialog from "./ReportScheduleDialog.jsx";

// ── Palette (matches the dashboard modules) ────────────────────────
const BLUE  = "#378ADD";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const PINK  = "#D4537E";

// ── Column config ──────────────────────────────────────────────────
const COLS = [
    // Name leads — it's how people identify a schedule once there are a dozen
    // of them off the same report. Description rides underneath it.
    { key: "scheduleName",   label: "Schedule",    width: 260, align: "left"  },
    { key: "reportName",     label: "Report",      width: 170, align: "left"  },
    { key: "recurrence",     label: "Recurrence",  width: 160, align: "left"  },
    { key: "generationTime", label: "Runs at",     width: 90,  align: "left"  },
    { key: "granularity",    label: "Granularity", width: 100, align: "left"  },
    { key: "timeRange",      label: "Time range",  width: 120, align: "left"  },
    { key: "outputs",        label: "Outputs",     width: 100, align: "left"  },
    { key: "recipients",     label: "Recipients",  width: 190, align: "left"  },
    { key: "lastRunDate",    label: "Last run",    width: 130, align: "right" },
    { key: "isEnabled",      label: "Active",      width: 70,  align: "left"  },
];

// 96px action column at the start (edit / run now / delete)
const TABLE_COLS  = [{ key: "_actions", label: "", width: 96, align: "left" }, ...COLS];
const GRID_COLS   = TABLE_COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH = TABLE_COLS.reduce((s, c) => s + c.width, 0) + 32;

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
    if (!val) return null;
    if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;
    if (typeof val === "string") {
        const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    }
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? null : d;
}

const formatDateTime = (val) => {
    const d = val ? new Date(val) : null;
    if (!d || Number.isNaN(d.getTime())) return "—";
    const day = parseLocalDate(val);
    const mm  = String(day.getMonth() + 1).padStart(2, "0");
    const dd  = String(day.getDate()).padStart(2, "0");
    const hh  = String(d.getHours()).padStart(2, "0");
    const mi  = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${day.getFullYear()} ${hh}:${mi}`;
};

function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

function Pill({ label, color, onClick, disabled, filled, icon }) {
    const inactive = !!disabled;
    return (
        <Box
            onClick={inactive ? undefined : onClick}
            sx={{
                px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                userSelect: "none", fontWeight: 500,
                display: "inline-flex", alignItems: "center", gap: "6px",
                border: filled ? "none" : "0.5px solid",
                borderColor: inactive ? "divider" : color,
                background: filled ? color : "transparent",
                color: filled ? "#fff" : (inactive ? "text.disabled" : color),
                cursor: inactive ? "default" : "pointer",
                opacity: inactive ? 0.5 : 1,
                transition: "all .15s",
                "&:hover": inactive ? {} : {
                    background: filled ? "#2a6db5" : "rgba(55,138,221,0.06)",
                },
            }}
        >
            {icon}{label}
        </Box>
    );
}

/**
 * Standing reminder of which clock the schedules run on, with the live
 * wall-clock time in that zone so "6:00 AM CT" is easy to reason about.
 */
function TimeZoneBadge({ tzKey }) {
    const tz = resolveTimeZone(tzKey);
    const [now, setNow] = useState(() => clockInZone(tz.iana));

    useEffect(() => {
        setNow(clockInZone(tz.iana));
        const id = setInterval(() => setNow(clockInZone(tz.iana)), 30000);
        return () => clearInterval(id);
    }, [tz.iana]);

    return (
        <Tooltip title={REPORT_STRINGS.TZ_SOURCE} arrow>
            <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                px: "12px", py: "6px", borderRadius: "20px",
                border: "0.5px solid", borderColor: "divider",
                background: "#f7f7f7",
                fontSize: 12, color: "text.secondary", cursor: "default",
            }}>
                <ScheduleIcon sx={{ fontSize: 14, color: BLUE }} />
                <Box component="span">
                    Reports run in{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
                        {tz.name} ({tz.key})
                    </Box>
                </Box>
                {now && (
                    <Box component="span" sx={{ color: "text.disabled" }}>· {now} there now</Box>
                )}
            </Box>
        </Tooltip>
    );
}

/** Small squared-off tag used for PDF / CSV. */
function Tag({ label, color }) {
    return (
        <Box component="span" sx={{
            fontSize: 10, fontWeight: 500, letterSpacing: "0.04em",
            textTransform: "uppercase",
            px: "6px", py: "2px", mr: "4px",
            borderRadius: "4px",
            color,
            border: "0.5px solid",
            borderColor: color,
            background: "transparent",
            whiteSpace: "nowrap",
        }}>
            {label}
        </Box>
    );
}

/**
 * Name on top, description underneath in a quieter size. Both clamp to one
 * line so row height stays fixed; the full description is in the tooltip.
 */
function ScheduleCell({ name, description }) {
    return (
        <Box sx={{ minWidth: 0, py: "2px" }}>
            <Box sx={{
                fontSize: 12, fontWeight: 500, color: "text.primary",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
                {name || "—"}
            </Box>
            {description ? (
                <Tooltip title={description} arrow placement="bottom-start">
                    <Box sx={{
                        fontSize: 11, color: "text.secondary", mt: "1px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        cursor: "default",
                    }}>
                        {description}
                    </Box>
                </Tooltip>
            ) : null}
        </Box>
    );
}

/** First address inline, remainder collapsed into "+n" with a tooltip. */
function RecipientCell({ recipients = [] }) {
    if (!recipients.length) return <Box component="span" sx={{ color: "text.disabled" }}>—</Box>;
    const [first, ...rest] = recipients;
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Box component="span" sx={{
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
                {first}
            </Box>
            {rest.length > 0 && (
                <Tooltip title={rest.join(", ")} arrow>
                    <Box component="span" sx={{
                        fontSize: 11, color: BLUE, fontWeight: 500,
                        cursor: "default", whiteSpace: "nowrap",
                    }}>
                        +{rest.length}
                    </Box>
                </Tooltip>
            )}
        </Box>
    );
}

// ── Component ──────────────────────────────────────────────────────
const ReportConfig = () => {
    const [catalog,    setCatalog]    = useState([]);
    const [schedules,  setSchedules]  = useState([]);
    const [tzKey,      setTzKey]      = useState(DEFAULT_TIME_ZONE_KEY);
    const [loading,    setLoading]    = useState(false);
    const [error,      setError]      = useState("");
    const [toast,      setToast]      = useState("");
    const [reportFilter, setReportFilter] = useState("all");
    const [page,       setPage]       = useState(0);
    const [sortKey,    setSortKey]    = useState("scheduleName");
    const [sortDir,    setSortDir]    = useState("asc");
    const [editing,    setEditing]    = useState(null);   // schedule object or null
    const [saving,     setSaving]     = useState(false);
    const [busyId,     setBusyId]     = useState(null);   // row-level spinner

    const load = useCallback(async () => {
        try {
            setLoading(true); setError(""); setPage(0);
            const [cat, list, tz] = await Promise.all([
                fetchReportCatalog(),
                fetchReportSchedules(),
                fetchClientTimeZone(),
            ]);
            setCatalog(cat || []);
            setSchedules(list || []);
            setTzKey(tz || DEFAULT_TIME_ZONE_KEY);
        } catch (e) {
            console.error(e);
            setError(REPORT_STRINGS.LOAD_FAILED);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Filter / sort / page ──────────────────────────────────────
    const filtered = useMemo(() => (
        reportFilter === "all"
            ? schedules
            : schedules.filter(s => String(s.reportId) === String(reportFilter))
    ), [schedules, reportFilter]);

    const sorted = useMemo(() => {
        if (!sortKey) return filtered;
        const valueOf = (row) => {
            if (sortKey === "recipients") return (row.recipients || []).length;
            if (sortKey === "isEnabled")  return row.isEnabled ? 1 : 0;
            if (sortKey === "recurrence") return describeRecurrence(row);
            return row[sortKey];
        };
        return [...filtered].sort((a, b) => {
            const av = valueOf(a), bv = valueOf(b);
            const an = Number(av), bn = Number(bv);
            const cmp = av !== "" && bv !== "" && !isNaN(an) && !isNaN(bn)
                ? an - bn
                : String(av ?? "").localeCompare(String(bv ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [filtered, sortKey, sortDir]);

    const paged      = useMemo(() =>
        sorted.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sorted, page]);
    const totalPages = Math.ceil(sorted.length / ROWS_PER_PAGE);

    const handleSortCol = (key) => {
        if (key === "_actions") return;
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    // ── KPIs ──────────────────────────────────────────────────────
    const kpis = useMemo(() => ({
        total:  schedules.length,
        active: schedules.filter(s => s.isEnabled).length,
        paused: schedules.filter(s => !s.isEnabled).length,
        // one recipient can appear on several schedules — count distinct people
        people: new Set(schedules.flatMap(s => s.recipients || [])).size,
    }), [schedules]);

    // ── Actions ───────────────────────────────────────────────────
    const handleSave = async (draft) => {
        try {
            setSaving(true); setError("");
            const saved = draft.scheduleId
                ? await updateReportSchedule(draft.scheduleId, draft)
                : await createReportSchedule(draft);

            setSchedules(prev => {
                if (!saved) return prev;
                return draft.scheduleId
                    ? prev.map(s => s.scheduleId === saved.scheduleId ? saved : s)
                    : [saved, ...prev];
            });
            if (!saved) await load();          // backend returned no body — refetch
            setEditing(null);
            setToast(draft.scheduleId ? "Schedule updated" : "Schedule created");
        } catch (e) {
            console.error(e);
            setError(REPORT_STRINGS.SAVE_FAILED);
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (row) => {
        const next = !row.isEnabled;
        setSchedules(prev => prev.map(s =>                 // optimistic
            s.scheduleId === row.scheduleId ? { ...s, isEnabled: next } : s));
        try {
            await toggleReportSchedule(row.scheduleId, next);
        } catch (e) {
            console.error(e);
            setSchedules(prev => prev.map(s =>             // roll back
                s.scheduleId === row.scheduleId ? { ...s, isEnabled: !next } : s));
            setError(REPORT_STRINGS.SAVE_FAILED);
        }
    };

    const handleDelete = async (row) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(REPORT_STRINGS.DELETE_CONFIRM)) return;
        try {
            setBusyId(row.scheduleId); setError("");
            await deleteReportSchedule(row.scheduleId);
            setSchedules(prev => prev.filter(s => s.scheduleId !== row.scheduleId));
            setToast("Schedule deleted");
        } catch (e) {
            console.error(e);
            setError(REPORT_STRINGS.DELETE_FAILED);
        } finally {
            setBusyId(null);
        }
    };

    const handleRunNow = async (row) => {
        try {
            setBusyId(row.scheduleId); setError("");
            await runReportNow(row.scheduleId);
            setToast(REPORT_STRINGS.RUN_QUEUED);
        } catch (e) {
            console.error(e);
            setError(REPORT_STRINGS.RUN_FAILED);
        } finally {
            setBusyId(null);
        }
    };

    const tz = resolveTimeZone(tzKey);

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
        columnGap: "8px",
    };

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Toolbar ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <Box>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                        Report
                    </Typography>
                    <Select
                        size="small"
                        value={reportFilter}
                        onChange={(e) => { setReportFilter(e.target.value); setPage(0); }}
                        sx={{ minWidth: 220, fontSize: 13, borderRadius: "20px" }}
                    >
                        <MenuItem value="all" sx={{ fontSize: 13 }}>All reports</MenuItem>
                        {catalog.map(r => (
                            <MenuItem key={r.reportId} value={String(r.reportId)} sx={{ fontSize: 13 }}>
                                {r.reportName}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <Pill
                    label={REPORT_STRINGS.ADD_SCHEDULE}
                    color={BLUE}
                    filled
                    disabled={loading || catalog.length === 0}
                    onClick={() => setEditing({ ...NEW_SCHEDULE })}
                />
                <Pill label="Refresh" color={BLUE} disabled={loading} onClick={load} />

                <Box sx={{ ml: "auto", alignSelf: "flex-end" }}>
                    <TimeZoneBadge tzKey={tzKey} />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

            {loading && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loading && schedules.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {REPORT_STRINGS.NO_SCHEDULES}
                </Typography>
            )}

            {!loading && schedules.length > 0 && (
                <>
                    {/* ── Summary ── */}
                    <SectionTitle>Summary</SectionTitle>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {[
                            { label: "Schedules",         value: kpis.total,  color: BLUE  },
                            { label: "Active",            value: kpis.active, color: GREEN },
                            { label: "Paused",            value: kpis.paused, color: AMBER },
                            { label: "Unique recipients", value: kpis.people, color: PINK  },
                        ].map(k => (
                            <Box key={k.label} sx={{
                                background: "#f7f7f7", borderRadius: 2,
                                p: "10px 14px", flex: 1, minWidth: 0,
                            }}>
                                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                                    {k.label}
                                </Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                                    {k.value.toLocaleString()}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* ── Table ── */}
                    <SectionTitle>Schedules</SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                            alignItems: "center",
                        }}>
                            {TABLE_COLS.map(col => (
                                <Box
                                    key={col.key}
                                    onClick={() => handleSortCol(col.key)}
                                    title={col.key === "generationTime" ? `${tz.name} (${tz.key})` : undefined}
                                    sx={{
                                        textAlign: col.align,
                                        cursor: col.key === "_actions" ? "default" : "pointer",
                                        userSelect: "none",
                                        display: "flex", gap: 0.5, alignItems: "center",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        color: sortKey === col.key ? BLUE : "text.secondary",
                                        "&:hover": col.key === "_actions" ? {} : { color: BLUE },
                                    }}
                                >
                                    {col.key === "generationTime" ? `Runs at (${tz.key})` : col.label}
                                    {sortKey === col.key && (
                                        <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Rows */}
                        {paged.map((row, i) => (
                            <Box key={row.scheduleId ?? i} sx={{
                                ...gridSx, py: "7px", fontSize: 12,
                                borderBottom: "0.5px solid", borderColor: "divider",
                                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                opacity: row.isEnabled ? 1 : 0.6,
                                "&:hover": { background: "rgba(55,138,221,0.06)" },
                                alignItems: "center",
                            }}>
                                {/* Actions */}
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {busyId === row.scheduleId ? (
                                        <CircularProgress size={16} sx={{ ml: "6px" }} />
                                    ) : (
                                        <>
                                            <Tooltip title="Edit schedule" arrow>
                                                <IconButton size="small" sx={{ width: 26, height: 26 }}
                                                    onClick={() => setEditing({ ...row })}>
                                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Run now and send" arrow>
                                                <IconButton size="small" sx={{ width: 26, height: 26 }}
                                                    onClick={() => handleRunNow(row)}>
                                                    <PlayArrowIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete schedule" arrow>
                                                <IconButton size="small" sx={{ width: 26, height: 26 }}
                                                    onClick={() => handleDelete(row)}>
                                                    <DeleteOutlineIcon sx={{ fontSize: 15, color: PINK }} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>

                                {COLS.map(col => (
                                    <Box key={col.key} sx={{
                                        textAlign: col.align,
                                        display: "flex", alignItems: "center", minWidth: 0,
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        ...(col.key === "reportName" && { color: BLUE }),
                                        ...(col.key === "lastRunDate" && { color: "text.secondary" }),
                                    }}>
                                        {col.key === "scheduleName" ? (
                                            <ScheduleCell name={row.scheduleName} description={row.description} />
                                        )
                                            : col.key === "recurrence" ? (
                                            <Box component="span" sx={{
                                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                                color: row.recurrence === RECURRENCE.NONE ? AMBER : "inherit",
                                            }}>
                                                {describeRecurrence(row)}
                                            </Box>
                                        )
                                            : col.key === "generationTime" ? formatClock(row.generationTime)
                                            : col.key === "granularity" ? (GRANULARITY_LABEL[row.granularity] || row.granularity)
                                            : col.key === "timeRange"   ? (TIME_RANGE_LABEL[row.timeRange] || row.timeRange)
                                            : col.key === "outputs"     ? (
                                                <>
                                                    {row.includePdf && <Tag label="PDF" color={BLUE}  />}
                                                    {row.includeCsv && <Tag label="CSV" color={GREEN} />}
                                                    {!row.includePdf && !row.includeCsv && "—"}
                                                </>
                                            )
                                            : col.key === "recipients"  ? <RecipientCell recipients={row.recipients} />
                                            : col.key === "lastRunDate" ? (
                                                <Tooltip title={row.lastRunStatus || ""} arrow>
                                                    <Box component="span" sx={{
                                                        whiteSpace: "nowrap",
                                                        color: row.lastRunStatus === "failed" ? PINK : "inherit",
                                                    }}>
                                                        {formatDateTime(row.lastRunDate)}
                                                    </Box>
                                                </Tooltip>
                                            )
                                            : col.key === "isEnabled"   ? (
                                                <Switch
                                                    size="small"
                                                    checked={!!row.isEnabled}
                                                    onChange={() => handleToggle(row)}
                                                />
                                            )
                                            : (
                                                <Box component="span" sx={{
                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                }}>
                                                    {row[col.key] || "—"}
                                                </Box>
                                            )}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <Box sx={{
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", mt: 1, px: 1,
                        }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, sorted.length)} of {sorted.length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                                <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page === 0 ? "default" : "pointer",
                                    opacity: page === 0 ? 0.4 : 1,
                                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                                }}>‹</Box>
                                <span>Page {page + 1} of {totalPages}</span>
                                <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                                }}>›</Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Add / edit dialog */}
            {editing && (
                <ReportScheduleDialog
                    open={!!editing}
                    schedule={editing}
                    catalog={catalog}
                    tzKey={tzKey}
                    saving={saving}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                />
            )}

            <Snackbar
                open={!!toast}
                autoHideDuration={3000}
                onClose={() => setToast("")}
                message={toast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </Box>
    );
};

export default ReportConfig;